import React, {useMemo, useState, useEffect} from 'react'
import {Modal, Button} from 'react-bootstrap'
import './ChangePlanConfirmModal.scss'
import {
  useGetUpcomingInvoiceChangePlanQuery,
  useChangePlanMutation,
} from '../../../../../services/cellcast.v2'
import {
  PreviewType,
  ProductPlan,
  Subscription,
  TaxRate,
  UpcomingInvoiceChangePlanData,
} from '../../../../../services/cellcast.v2.types'
import {formatMoney} from '../../contact-collection/utils/money'
type ChangePlanConfirmModalProps = {
  show: boolean
  onClose: () => void
  onConfirm?: (applyNow: boolean) => void
  currentPlan?: ProductPlan | null
  newPlan?: ProductPlan | null
  nextBillingDate?: string
  isUpdate?: boolean
  subscriptionId?: string
  subscription?: Subscription
  onBack?: () => void
}

const ChangePlanConfirmModal: React.FC<ChangePlanConfirmModalProps> = ({
  show,
  onClose,
  onConfirm,
  currentPlan,
  newPlan,
  nextBillingDate = new Date().toLocaleDateString(),
  isUpdate = false,
  subscriptionId = '',
  subscription,
  onBack,
}) => {
  const [applyNow, setApplyNow] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [changePlan] = useChangePlanMutation()

  const previewArgs = {
    currentPlanId: currentPlan?._id || '',
    newPlanId: newPlan?._id || '',
    applyNow,
    subscriptionId,
    previewType: PreviewType.subscription_change,
    planId: null,
    isTrial: subscription?.status === 'trialing',
  }

  const shouldSkip = !previewArgs.currentPlanId || !previewArgs.newPlanId
  const {
    data: preview,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useGetUpcomingInvoiceChangePlanQuery(previewArgs, {
    skip: shouldSkip,
  })

  useEffect(() => {
    if (show && !shouldSkip) {
      refetch()
    }
  }, [show, currentPlan?._id, newPlan?._id, applyNow, refetch, shouldSkip])

  const changePreview = preview?.data
    ?.changePreview as UpcomingInvoiceChangePlanData['changePreview']

  const totalToday = useMemo(() => {
    if (!applyNow) return '$0.00'
    const total = changePreview?.proration?.formatted?.total
    return total || '—'
  }, [applyNow, changePreview])

  const currency = changePreview?.pricing?.currency

  const BillingSummarySkeleton = () => (
    <div className='rounded-3 p-3 cpcm-summary'>
      {/* Header */}
      <div className='d-flex justify-content-between align-items-start mb-3'>
        <div>
          <div className='bg-light rounded mb-2' style={{height: 16, width: 100}}></div>
          <div className='bg-light rounded' style={{height: 12, width: 80}}></div>
        </div>
        <div className='text-end'>
          <div className='bg-light rounded mb-2 ms-auto' style={{height: 12, width: 120}}></div>
          <div className='bg-light rounded ms-auto' style={{height: 18, width: 80}}></div>
        </div>
      </div>

      {/* Rows */}
      <div className='mt-3'>
        {[
          {left: 120, right: 60},
          {left: 100, right: 50},
          {left: 140, right: 70},
          {left: 90, right: 80},
        ].map((row, i) => (
          <div key={i} className='d-flex justify-content-between align-items-center py-1'>
            <div className='bg-light rounded' style={{height: 14, width: row.left}}></div>
            <div className='bg-light rounded' style={{height: 14, width: row.right}}></div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className='mt-3'>
        <div className='bg-light rounded' style={{height: 12, width: 200}}></div>
      </div>
    </div>
  )

  return (
    <Modal show={show} onHide={onClose} centered size='lg' backdrop='static'>
      <Modal.Header closeButton>
        <h2 className='fw-bold cpcm-title'>Confirm plan change</h2>
      </Modal.Header>

      <Modal.Body className='px-4 py-4'>
        <div className='rounded-3 p-3 mb-4 cpcm-callout'>
          <div className='d-flex align-items-start gap-3'>
            <div style={{fontSize: 18}}>⚡</div>
            <div className='cpcm-callout-text'>
              <div className='fw-bold text-dark mb-1 cpcm-callout-title'>
                Changes take effect immediately with pro-rata billing.
              </div>
              <span>
                You'll receive a credit for unused time on your current plan, and only pay the
                difference today.
              </span>
            </div>
          </div>
        </div>

        <div className='row g-3 mb-4'>
          <div className='col-12 col-md-6'>
            <div className='rounded-3 p-3 cpcm-card'>
              <div className='text-uppercase text-muted' style={{fontSize: 11, fontWeight: 600}}>
                Current
              </div>
              <div className='fw-bold text-dark mt-1 cpcm-text-dark-16-700'>
                {currentPlan?.name} — {formatMoney(currentPlan?.price, currency)}
              </div>
              <div className='text-muted mt-1 cpcm-text-muted-14'>
                {currentPlan?.features.join(' • ')}
              </div>
            </div>
          </div>
          <div className='col-12 col-md-6'>
            <div className='rounded-3 p-3 cpcm-card-selected'>
              <div className='d-flex align-items-center justify-content-between'>
                <div>
                  <div className='text-uppercase fw-bold' style={{fontSize: 11, color: '#7c3aed'}}>
                    New plan
                  </div>
                  <div className='fw-bold text-dark mt-1 cpcm-text-dark-16-700'>
                    {newPlan?.name} — {formatMoney(newPlan?.price, currency)}
                  </div>
                </div>
                <span className='rounded-pill px-2 py-1 cpcm-badge-selected'>Selected</span>
              </div>
              <div className='text-muted mt-1 cpcm-text-muted-14'>
                {newPlan?.features.join(' • ')}
              </div>
            </div>
          </div>
        </div>

        <div className='rounded-3 p-3 mb-4 cpcm-card'>
          <div className='fw-bold text-dark' style={{fontSize: 14, fontWeight: '700'}}>
            When should this change apply?
          </div>
          <div className='row g-3 mt-2'>
            <div className='col-12 col-md-6'>
              <label
                className={`w-100 d-flex gap-3 rounded-3 p-3 ${
                  applyNow ? 'cpcm-option cpcm-option--active' : 'cpcm-option'
                }`}
              >
                <input
                  type='radio'
                  name='when'
                  checked={applyNow}
                  onChange={() => setApplyNow(true)}
                  className='cpcm-radio'
                />
                <div style={{fontSize: 14}}>
                  <div className='fw-bold text-dark' style={{fontWeight: '700'}}>
                    Apply now (recommended)
                  </div>
                  <div className='text-muted' style={{fontWeight: '500'}}>
                    Pro-rata credit applied today. New features unlock immediately.
                  </div>
                </div>
              </label>
            </div>
            <div className='col-12 col-md-6'>
              <label
                className={`w-100 d-flex gap-3 rounded-3 p-3 ${
                  !applyNow ? 'cpcm-option cpcm-option--active' : 'cpcm-option'
                }`}
              >
                <input
                  type='radio'
                  name='when'
                  checked={!applyNow}
                  onChange={() => setApplyNow(false)}
                  className='cpcm-radio'
                />
                <div style={{fontSize: 14}}>
                  <div className='fw-bold text-dark' style={{fontWeight: '700'}}>
                    On next renewal
                  </div>
                  <div className='text-muted' style={{fontWeight: '500'}}>
                    Keeps current plan until {nextBillingDate}. No charge today.
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Billing summary */}
        {isLoading || isFetching ? (
          <BillingSummarySkeleton />
        ) : (
          <div className='rounded-3 p-3 cpcm-summary'>
            <div className='d-flex justify-content-between'>
              <div>
                <div className='fw-bold text-dark' style={{fontSize: 14, fontWeight: '700'}}>
                  Billing summary
                </div>
                <div className='text-muted' style={{fontSize: 12, fontWeight: '500'}}>
                  {applyNow ? 'Today' : 'At next renewal'}
                </div>
              </div>
              <div className='text-end'>
                <div className='text-muted' style={{fontSize: 12, fontWeight: '500'}}>
                  {applyNow ? 'Estimated total today' : 'Estimated change at renewal'}
                </div>
                <div className='fw-bold text-dark' style={{fontSize: 18, fontWeight: '700'}}>
                  {applyNow
                    ? formatMoney(totalToday, currency)
                    : formatMoney(changePreview?.pricing?.total, currency)}
                </div>
              </div>
            </div>

            {/* Credit/Refund or Additional Payment Indicators */}
            {changePreview && (
              <>
                {/* Credit/Refund Indicator (Green) */}
                {applyNow &&
                  changePreview.proration?.total &&
                  Number(changePreview.proration.total) < 0 && (
                    <div
                      className='alert alert-success d-flex align-items-center mt-3 mb-0'
                      style={{fontSize: 14}}
                    >
                      <i className='bi bi-check-circle-fill me-2'></i>
                      <div>
                        <strong>
                          You have been credited{' '}
                          {formatMoney(Math.abs(Number(changePreview.proration.total)), currency)}.
                        </strong>
                        <br />
                        <small>This amount will be applied to your next invoice.</small>
                      </div>
                    </div>
                  )}
              </>
            )}

            <div className='table-responsive mt-3'>
              {error && (
                <div className='text-danger mb-2' style={{fontSize: 12}}>
                  Failed to load proration preview.
                </div>
              )}
              <table className='table mb-0'>
                <tbody>
                  {applyNow ? (
                    <>
                      <tr>
                        <td className='text-muted' style={{fontSize: 14, fontWeight: 500}}>
                          Prorated subtotal
                        </td>
                        <td className='text-end' style={{fontSize: 14, fontWeight: 600}}>
                          {!changePreview
                            ? '—'
                            : formatMoney(
                                changePreview.proration?.subtotal ??
                                  changePreview.proration?.formatted?.subtotal ??
                                  '—',
                                currency
                              )}
                        </td>
                      </tr>
                      <tr>
                        <td className='text-muted' style={{fontSize: 14, fontWeight: 500}}>
                          {changePreview?.taxRates?.map((taxRate: TaxRate) => (
                            <span key={taxRate.id}>
                              {taxRate.displayName} ({taxRate.percentage}%)
                            </span>
                          ))}
                        </td>
                        <td className='text-end' style={{fontSize: 14, fontWeight: 600}}>
                          {!changePreview
                            ? '—'
                            : formatMoney(
                                changePreview.proration?.tax ??
                                  changePreview.proration?.formatted?.tax ??
                                  '—',
                                currency
                              )}
                        </td>
                      </tr>
                      {changePreview?.nextBilling?.amount !== undefined && (
                        <tr>
                          <td className='text-muted' style={{fontSize: 14, fontWeight: 500}}>
                            Next renewal (after today)
                          </td>
                          <td className='text-end' style={{fontSize: 14, fontWeight: 600}}>
                            {formatMoney(changePreview?.nextBilling?.amount, currency)} /{' '}
                            {changePreview?.newPlan?.interval}
                          </td>
                        </tr>
                      )}
                      <tr className='cpcm-summary-row-subtle'>
                        <td className='fw-bold text-dark' style={{fontSize: 14, fontWeight: 700}}>
                          Total due today
                        </td>
                        <td
                          className='text-end fw-bold text-dark'
                          style={{fontSize: 14, fontWeight: 700}}
                        >
                          {formatMoney(totalToday, currency)}
                        </td>
                      </tr>
                    </>
                  ) : (
                    <>
                      <tr>
                        <td className='text-muted' style={{fontSize: 14, fontWeight: 500}}>
                          Current plan
                        </td>
                        <td className='text-end' style={{fontSize: 14, fontWeight: 600}}>
                          {currentPlan?.name} — {formatMoney(currentPlan?.price, currency)} /{' '}
                          {currentPlan?.billingInterval}
                        </td>
                      </tr>
                      <tr>
                        <td className='text-muted' style={{fontSize: 14, fontWeight: 500}}>
                          New plan (at renewal)
                        </td>
                        <td className='text-end' style={{fontSize: 14, fontWeight: 600}}>
                          {newPlan?.name} — {formatMoney(newPlan?.price, currency)} /{' '}
                          {newPlan?.billingInterval}
                        </td>
                      </tr>
                      {/* tax rates */}
                      {changePreview?.taxRates?.map((taxRate: TaxRate) => (
                        <tr key={taxRate.id}>
                          <td className='text-muted' style={{fontSize: 14, fontWeight: 500}}>
                            {taxRate.displayName} ({taxRate.percentage}%)
                          </td>
                          <td className='text-end' style={{fontSize: 14, fontWeight: 600}}>
                            {taxRate.percentage}%
                          </td>
                        </tr>
                      ))}

                      <tr className='cpcm-summary-row-subtle'>
                        <td className='fw-bold text-dark' style={{fontSize: 14, fontWeight: 700}}>
                          Total due today
                        </td>
                        <td
                          className='text-end fw-bold text-dark'
                          style={{fontSize: 14, fontWeight: 700}}
                        >
                          {formatMoney(0, currency)}
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
            <div className='mt-2 text-muted' style={{fontSize: 12, fontWeight: '500'}}>
              {applyNow ? 'Effective on:' : 'Next regular billing date:'}{' '}
              <span className='fw-bold text-dark' style={{fontWeight: '700'}}>
                {applyNow
                  ? new Date(changePreview?.effectiveDate || nextBillingDate).toLocaleDateString()
                  : nextBillingDate}
              </span>
            </div>
          </div>
        )}

        <div className='text-muted mt-3 cpcm-text-muted-14' style={{fontSize: 12}}>
          By confirming, you agree to the updated terms for your {newPlan?.name} subscription. Usage
          & quotas relate to {newPlan?.name} only; messaging is billed separately.
        </div>
      </Modal.Body>

      <Modal.Footer className='d-flex justify-content-end'>
        <div className='d-flex gap-2'>
          <Button
            variant='outline-secondary'
            onClick={onBack}
            className='rounded-3 cpcm-btn-cancel-outline'
          >
            Back
          </Button>
          <Button
            className='rounded-3 cpcm-btn-primary'
            onClick={async () => {
              onConfirm?.(applyNow)
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span
                  className='spinner-border spinner-border-sm me-2'
                  role='status'
                  aria-hidden='true'
                ></span>
                Processing...
              </>
            ) : (
              'Confirm change'
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default ChangePlanConfirmModal
